import { useState } from 'react';
import type { FC } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid';
import ContactEditForm from './ContactEditForm';
import ContactCreateForm from './ContactCreateForm';
import { defaultCustomerContactsModel, CustomerContactsModel } from 'src/types/CustomerContactsModel';
import api from '../../api/Api';
import { Box, CircularProgress, Button } from '@mui/material';
import PageContainerLayout from 'src/pages/PageContainerLayout';
import useOnMount from 'src/hooks/useOnMount';
import useSnackbar from 'src/hooks/useSnackbar';

const Contacts: FC<React.PropsWithChildren<unknown>> = () => {
  const { customerNumber } = useParams();
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [openCreateForm, setOpenCreateForm] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [contact, setContact] = useState<CustomerContactsModel>({
    ...defaultCustomerContactsModel,
  });
  const { openSnackBar } = useSnackbar();
  const [data, setData] = useState<CustomerContactsModel[]>([]);

  function loadContacts() {
    setIsLoading(true);
    api
      .get<CustomerContactsModel[]>(`/customer/${customerNumber}/contacts`)
      .then((contactList: CustomerContactsModel[]) => {
        setData(contactList);
        setOpenForm(false);
        setOpenCreateForm(false);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => setIsLoading(false));
  }

  useOnMount(() => {
    loadContacts();
  });

  const enterEdit = (item: any) => {
    setOpenForm(true);
    setContact(item);
  };

  const enterCreate = () => {
    setOpenCreateForm(true);
  };

  const handleCancel = () => {
    setOpenForm(false);
    setOpenCreateForm(false);
    setIsLoading(false);
  };

  const handleSubmit = () => {
    setIsLoading(true);
  };

  const onSuccess = () => {
    openSnackBar('Success! Please refresh the page after a few moments to see your changes. May take longer during busy periods.', 'info', 6000);
    loadContacts();
    setIsLoading(false);
    setOpenForm(false);
    setOpenCreateForm(false);
  };

  interface EditCommandCellProps extends GridCellProps {
    enterEdit: (item: CustomerContactsModel) => void;
  }

  const EditCommandCell = (props: EditCommandCellProps) => {
    return (
      <td style={{ whiteSpace: 'nowrap' }}>
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <button className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary" onClick={() => props.enterEdit(props.dataItem)}>
            Edit
          </button>
        </div>
      </td>
    );
  };

  const MyEditCommandCell = (props: GridCellProps) => <EditCommandCell {...props} enterEdit={enterEdit} />;

  return (
    <PageContainerLayout pageHeaderTitle="Contacts">
      <Box>
        <Button sx={{ mb: 2, mr: 2 }} variant="contained" onClick={enterCreate}>
          Create Contact
        </Button>
        {isLoading ? <CircularProgress size={25} /> : <></>}
      </Box>
      <Box>
        {data.length > 0 && (
          <div>
            <Grid data={data} scrollable="scrollable">
              <GridColumn cells={{ data: MyEditCommandCell }} width={55} />
              <GridColumn field="firstName" title="First Name" width={110} />
              <GridColumn field="lastName" title="Last Name" width={160} />
              <GridColumn field="title" title="Title" width={150} />
              <GridColumn field="email" title="Email" width={170} />
              <GridColumn field="phoneNumber" title="Work Phone" width={150} />
              <GridColumn field="faxNumber" title="Fax Number" width={150} />
              <GridColumn field="address1" title="Address" width={170} />
              <GridColumn field="city" title="City" width={120} />
              <GridColumn field="state" title="State" width={80} />
              <GridColumn field="zipcode" title="Zip" width={60} />
              <GridColumn field="" title="" width="" filterable={false} columnMenu={null} />
            </Grid>
          </div>
        )}

        <>
          {openForm && (
            <ContactEditForm isOpen={openForm} onSubmit={handleSubmit} onCancel={handleCancel} onSuccess={onSuccess} contact={contact} title={`Edit ${contact.firstName}`} />
          )}
          {openCreateForm && <ContactCreateForm isOpen={openCreateForm} onSubmit={handleSubmit} onCancel={handleCancel} onSuccess={onSuccess} title={'Create new Contact'} />}
        </>
      </Box>
    </PageContainerLayout>
  );
};

export default Contacts;
